import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface CreditsState {
  balance: number;
  totalEarned: number;
  totalSpent: number;
  transactions: Array<{
    id: string;
    amount: number;
    type: string;
    description: string;
    created_at: string;
  }>;
  loading: boolean;
  fetchCredits: (userId: string) => Promise<void>;
  fetchTransactions: (userId: string) => Promise<void>;
  awardCredits: (userId: string, amount: number, type: string, description: string) => Promise<void>;
  spendCredits: (userId: string, amount: number, type: string, description: string) => Promise<void>;
}

export const useCreditsStore = create<CreditsState>((set, get) => ({
  balance: 0,
  totalEarned: 0,
  totalSpent: 0,
  transactions: [],
  loading: false,

  fetchCredits: async (userId) => {
    if (!userId) {
      console.warn('[Credits] No userId provided, skipping fetch');
      set({ balance: 0, totalEarned: 0, totalSpent: 0, loading: false });
      return;
    }

    set({ loading: true });
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        console.log('[Credits] Fetching credits for user:', userId, retryCount > 0 ? `(retry ${retryCount})` : '');
        const { data, error } = await supabase
          .from('credits')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) {
          console.error('[Credits] Error fetching credits:', error);
          if (retryCount < maxRetries - 1) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          }
          set({ balance: 0, totalEarned: 0, totalSpent: 0 });
          return;
        }

        if (data) {
          console.log('[Credits] Credits loaded:', data.balance);
          set({
            balance: data.balance || 0,
            totalEarned: data.total_earned || 0,
            totalSpent: data.total_spent || 0,
          });
          return;
        } else {
          console.warn('[Credits] No credits record found for user, creating one...');
          const { data: newCredits, error: insertError } = await supabase
            .from('credits')
            .insert({
              user_id: userId,
              balance: 100,
              total_earned: 100,
              total_spent: 0,
            })
            .select()
            .single();

          if (insertError) {
            console.error('[Credits] Error creating credits record:', insertError);
            if (retryCount < maxRetries - 1) {
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
              continue;
            }
            set({ balance: 0, totalEarned: 0, totalSpent: 0 });
          } else {
            console.log('[Credits] Credits record created successfully');
            set({
              balance: newCredits?.balance || 100,
              totalEarned: newCredits?.total_earned || 100,
              totalSpent: newCredits?.total_spent || 0
            });
          }
          return;
        }
      } catch (error) {
        console.error('[Credits] Exception during fetchCredits:', error);
        if (retryCount < maxRetries - 1) {
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        } else {
          set({ balance: 0, totalEarned: 0, totalSpent: 0 });
          return;
        }
      }
    }

    set({ loading: false });
  },

  fetchTransactions: async (userId) => {
    if (!userId) {
      console.warn('[Credits] No userId provided for transactions fetch');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('[Credits] Error fetching transactions:', error);
        set({ transactions: [] });
        return;
      }

      set({ transactions: data || [] });
    } catch (error) {
      console.error('[Credits] Exception during fetchTransactions:', error);
      set({ transactions: [] });
    }
  },

  awardCredits: async (userId, amount, type, description) => {
    if (!userId || amount <= 0) {
      console.warn('[Credits] Invalid parameters for awardCredits');
      return;
    }

    try {
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: userId,
          amount,
          type,
          description,
        });

      if (transactionError) {
        // If it's a duplicate transaction error, log and return gracefully
        // This means the credit was already awarded for this action
        if (transactionError.code === '23505') {
          console.warn('[Credits] Duplicate transaction detected (credit already awarded):', description);
          return;
        }
        console.error('[Credits] Transaction insert error:', transactionError);
        throw transactionError;
      }

      const { data: credits } = await supabase
        .from('credits')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (credits) {
        const { error: updateError } = await supabase
          .from('credits')
          .update({
            balance: credits.balance + amount,
            total_earned: credits.total_earned + amount,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', userId);

        if (updateError) {
          console.error('[Credits] Update error:', updateError);
          throw updateError;
        }
      }

      await get().fetchCredits(userId);
      await get().fetchTransactions(userId);
    } catch (error) {
      console.error('[Credits] Exception during awardCredits:', error);
      throw error;
    }
  },

  spendCredits: async (userId, amount, type, description) => {
    if (!userId || amount <= 0) {
      console.warn('[Credits] Invalid parameters for spendCredits');
      throw new Error('Invalid parameters');
    }

    try {
      const { data: credits } = await supabase
        .from('credits')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (!credits) {
        console.error('[Credits] No credits record found for user');
        throw new Error('Credits record not found');
      }

      if (credits.balance < amount) {
        console.warn('[Credits] Insufficient balance:', credits.balance, 'needed:', amount);
        throw new Error('Insufficient credits');
      }

      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: userId,
          amount: -amount,
          type,
          description,
        });

      if (transactionError) {
        // If it's a duplicate transaction error, log and return gracefully
        if (transactionError.code === '23505') {
          console.warn('[Credits] Duplicate spend transaction detected:', description);
          return;
        }
        console.error('[Credits] Transaction error:', transactionError);
        throw transactionError;
      }

      const { error: updateError } = await supabase
        .from('credits')
        .update({
          balance: credits.balance - amount,
          total_spent: credits.total_spent + amount,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('[Credits] Update error:', updateError);
        throw updateError;
      }

      await get().fetchCredits(userId);
      await get().fetchTransactions(userId);
    } catch (error) {
      console.error('[Credits] Exception during spendCredits:', error);
      throw error;
    }
  },
}));
